import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../routes/paths';
import ConfirmModal from './ConfirmModal';
import { getAdminInboxUnreadCountApi } from '../../api';
import { io } from 'socket.io-client';
import { ADMIN_NOTIFICATION_PREF_EVENT, ADMIN_NOTIFICATION_PREF_KEY } from '../../constants/notifications';

const DEFAULT_SOCKET_BASE_URL = (() => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    const protocol = window.location.protocol || 'http:';
    return `${protocol}//${host}:4000`;
  }
  return 'http://localhost:4000';
})();

const SOCKET_BASE_URL = (() => {
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  if (apiBase) {
    return apiBase.replace(/\/api\/?$/, '');
  }
  return DEFAULT_SOCKET_BASE_URL;
})();

const Header = ({
  title = 'Manila701',
  variant = 'full',
  onClickMenu,
}) => {
  const isSimple = variant === 'simple';
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const socketRef = useRef(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const fetchUnreadCount = useCallback(async () => {
    if (currentUser?.role !== 'ADMIN') {
      setAdminUnreadCount(0);
      return;
    }

    try {
      const response = await getAdminInboxUnreadCountApi();
      const countSource = response?.data ?? response;
      const count =
        typeof countSource === 'number'
          ? countSource
          : Number(countSource?.count ?? 0);
      setAdminUnreadCount(Number.isFinite(count) ? count : 0);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch admin unread inbox count', err);
      }
    }
  }, [currentUser?.role]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) {
        setCurrentUser(JSON.parse(raw));
      } else {
        setCurrentUser(null);
      }
    } catch {
      setCurrentUser(null);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.role !== 'ADMIN') {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const socket = io(SOCKET_BASE_URL, {
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;
    socket.emit('join_room', 'admin_inbox');

    const handleInboxUpdated = () => {
      fetchUnreadCount();
    };

    const handleThreadCleared = () => {
      fetchUnreadCount();
    };

    socket.on('inbox_updated', handleInboxUpdated);
    socket.on('thread_cleared', handleThreadCleared);

    return () => {
      socket.off('inbox_updated', handleInboxUpdated);
      socket.off('thread_cleared', handleThreadCleared);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [currentUser?.role, fetchUnreadCount]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const loadPref = () => {
      try {
        const stored = window.localStorage.getItem(ADMIN_NOTIFICATION_PREF_KEY);
        setNotificationsEnabled(stored !== 'off');
      } catch (err) {
        setNotificationsEnabled(true);
      }
    };

    loadPref();

    const handleStorage = (event) => {
      if (event?.key === ADMIN_NOTIFICATION_PREF_KEY) {
        loadPref();
      }
    };

    const handlePrefEvent = (event) => {
      if (typeof event?.detail === 'boolean') {
        setNotificationsEnabled(event.detail);
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(ADMIN_NOTIFICATION_PREF_EVENT, handlePrefEvent);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(ADMIN_NOTIFICATION_PREF_EVENT, handlePrefEvent);
    };
  }, []);

  useEffect(() => {
    let intervalId;

    fetchUnreadCount();

    if (currentUser?.role === 'ADMIN') {
      intervalId = window.setInterval(fetchUnreadCount, 30000);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [currentUser?.role, fetchUnreadCount]);

  const handleClickTitle = () => {
    navigate(PATHS.root);
  };

  const handleClickLogin = () => {
    navigate(PATHS.login);
  };

  const handleClickRegister = () => {
    navigate(PATHS.register);
  };

  const handleClickMyPage = () => {
    navigate(PATHS.myPage);
  };

  const handleClickInbox = () => {
    const isAdminUser = currentUser?.role === 'ADMIN';
    if (isAdminUser) {
      navigate(PATHS.inbox);
      return;
    }

    const currentUserId = currentUser?.id || currentUser?.user_id || currentUser?.userId;
    const roomId = currentUserId ? String(currentUserId) : 'me';
    navigate(PATHS.inboxRoom.replace(':roomId', roomId));
  };

  const handleLogout = () => {
    window.localStorage.removeItem('accessToken');
    window.localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setAdminUnreadCount(0);
    setLogoutOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutOpen(false);
    navigate(PATHS.root);
  };

  const isLoggedIn = Boolean(currentUser);
  const isAdmin = currentUser?.role === 'ADMIN';
  const displayName = isAdmin ? '관리자' : currentUser?.name || currentUser?.loginId || 'User';

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          pt: '28px',
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, cursor: 'pointer', fontSize: 32, display: 'flex', alignItems: 'center' }}
          onClick={handleClickTitle}
          component="div"
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/logo/Manila701_logo.png`}
            alt="Manila701"
            sx={{ height: 37, width: 'auto', display: 'block' }}
          />
        </Typography>

        {!isSimple && !isLoggedIn && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14 }}>
            <Typography
              variant="body2"
              sx={{ cursor: 'pointer' }}
              onClick={handleClickLogin}
            >
              로그인
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
              <Typography variant="body2" onClick={handleClickRegister}>
                회원가입
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: onClickMenu ? 'pointer' : 'default',
              }}
              onClick={onClickMenu}
            >
              <MenuIcon fontSize="small" />
            </Box>
          </Box>
        )}

        {!isSimple && isLoggedIn && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.9,
              fontSize: 14,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
              }}
              onClick={handleClickMyPage}
            >
              {displayName}님
            </Typography>
            <Typography
              variant="body2"
              sx={{
                cursor: 'pointer',
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
                padding: '2px 2px 2px 0',
              }}
              onClick={handleClickInbox}
            >
              <Box component="span">1:1문의</Box>
              {isAdmin && notificationsEnabled && adminUnreadCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: -3,
                    right: 0,
                    transform: 'translate(60%, -50%)',
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    mx: 0.5,
                    borderRadius: '9px',
                    bgcolor: '#ef4444',
                    color: '#ffffff',
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: '18px',
                    textAlign: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  }}
                >
                  {adminUnreadCount > 99 ? '99+' : adminUnreadCount}
                </Box>
              )}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
              }}
              onClick={handleLogout}
            >
              로그아웃
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: onClickMenu ? 'pointer' : 'default',
              }}
              onClick={onClickMenu}
            >
              <MenuIcon fontSize="small" />
            </Box>
          </Box>
        )}
      </Box>

      <ConfirmModal
        open={logoutOpen}
        message="로그아웃이 되었습니다."
        onConfirm={handleLogoutConfirm}
      />
    </>
  );
};

export default Header;